<template>
  <div class="form-section">
    <h3 class="text-responsive-base font-bold text-center mb-responsive text-gray-800">
      Review and Submit
    </h3>
    
    <!-- Form Validation Alert -->
    <div v-if="hasVisibleErrors" class="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
      <div class="flex">
        <div class="flex-shrink-0">
          <Icon icon="mdi:alert-circle" class="h-5 w-5 text-red-500" />
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-red-700">Please correct the following errors:</p>
          <ul class="mt-1 text-sm text-red-700 list-disc list-inside">
            <li v-if="errors.effectiveDate">Effective Date: {{ errors.effectiveDate }}</li>
            <li v-if="errors.signature">Signature: {{ errors.signature }}</li>
            <li v-if="errors.signatureDate">Signature Date: {{ errors.signatureDate }}</li>
            <li v-for="(error, key) in otherErrors" :key="key">{{ formatErrorKey(key) }}: {{ error }}</li>
          </ul>
        </div>
      </div>
    </div>
    
    <div class="space-y-responsive">
      <!-- Effective Date -->
      <div class="form-field">
        <ResponsiveDateInput
          label="Effective Date"
          field-id="effective-date"
          :model-value="effectiveDate"
          @update:model-value="$emit('update:effectiveDate', $event)"
          :min-date="minDate"
          :error="errors.effectiveDate"
          description="Please select the date you want these changes to take effect."
          is-primary
        />
        <!-- This specific help text is kept separate to preserve its unique styling -->
        <p class="text-orange-600 text-xs mt-1 font-semibold">
          Please note: This is your <strong>requested</strong> effective date. All policy changes are subject to review and carrier approval.
        </p>
      </div>
      
      <!-- Additional Comments -->
      <div class="form-field">
        <label for="comments" class="block text-responsive-sm font-semibold text-gray-700 mb-2">
          Additional Comments (Optional)
        </label>
        <textarea
          id="comments"
          :value="comments"
          @input="$emit('update:comments', $event.target.value)"
          rows="4"
          class="input-responsive resize-none"
          placeholder="Add any relevant details or special instructions..."
        ></textarea>
      </div>
      
      <!-- Electronic Signature -->
      <div class="form-field">
        <label for="signature" class="block text-responsive-sm font-semibold text-gray-700 mb-2">
          Electronic Signature <span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="signature"
          :value="signature"
          @input="$emit('update:signature', $event.target.value)"
          class="input-responsive"
          :class="{'border-red-500': errors.signature}"
          placeholder="Type your full name as your signature"
        />
        <p v-if="errors.signature" class="text-red-500 text-xs italic mt-1">
          {{ errors.signature }}
        </p>
        <p class="text-gray-500 text-xs mt-1">
          By typing your name, you are electronically signing this request.
        </p>
      </div>
      
      <!-- Signature Date -->
      <div class="form-field">
        <ResponsiveDateInput
          label="Signature Date"
          field-id="signature-date"
          :model-value="signatureDate"
          @update:model-value="$emit('update:signatureDate', $event)"
          :min-date="minDate"
          :error="errors.signatureDate"
          is-primary
          required
        />
      </div>
      
      <!-- Terms and Conditions -->
      <div class="form-field bg-gray-50 p-4 rounded-md border border-gray-200">
        <p class="text-sm text-gray-700 font-semibold mb-2">
          Important - Please Acknowledge:
        </p>
        <ul class="list-disc pl-5 text-sm text-gray-600 space-y-1">
          <li>The information provided is accurate to the best of your knowledge.</li>
          <li>This submission is a <strong>request</strong> for changes and not a confirmation of coverage adjustment.</li>
          <li>All requested changes are subject to review by our team and approval by your insurance carrier.</li>
          <li>We represent multiple insurance companies, and all policy adjustments are contingent upon their underwriting guidelines and approval.</li>
          <li>An agent may contact you to verify details or request additional information.</li>
          <li>Changes to your policy may result in premium adjustments.</li>
          <li>The requested effective date is not guaranteed and will be confirmed upon approval.</li>
        </ul>
      </div>
      
      <!-- Error Message Display -->
      <div v-if="errors.submission" class="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <Icon icon="mdi:alert-circle" class="h-5 w-5 text-red-500" />
          </div>
          <div class="ml-3">
            <p class="text-sm text-red-700">
              {{ errors.submission }}
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Action Buttons -->
    <div class="flex-responsive-col justify-between mt-responsive">
      <button
        @click="$emit('previous')"
        class="btn-responsive-secondary mb-2 sm:mb-0"
        :disabled="isSubmitting"
      >
        Back
      </button>
      <button
        @click="$emit('submit')"
        :class="customerType === 'lender' ? 'btn-lender' : 'btn-customer'"
        :disabled="isSubmitting"
      >
        <span v-if="isSubmitting">
          <Icon icon="eos-icons:loading" class="animate-spin mr-2" />
          Submitting...
        </span>
        <span v-else>Submit Request</span>
      </button>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import Icon from '@/components/common/Icon.vue';
import { getTodayFormatted } from '@/utils/dateUtils';
import ResponsiveDateInput from '@/components/common/ResponsiveDateInput.vue';

export default {
  name: 'EffectiveDateStep',
  components: {
    Icon,
    ResponsiveDateInput
  },
  props: {
    effectiveDate: {
      type: String,
      required: true
    },
    comments: {
      type: String,
      required: true
    },
    signature: {
      type: String,
      required: true
    },
    signatureDate: {
      type: String,
      required: true
    },
    customerType: {
      type: String,
      default: 'customer'
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    isSubmitting: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'update:effectiveDate',
    'update:comments',
    'update:signature',
    'update:signatureDate',
    'previous',
    'submit'
  ],
  setup(props) {
    // Calculate minimum date (today)
    const minDate = computed(() => getTodayFormatted());
    
    // Check if there are validation errors
    const hasVisibleErrors = computed(() => {
      return Object.keys(props.errors).some(key => 
        ['effectiveDate', 'signature', 'signatureDate'].includes(key) && 
        props.errors[key]
      );
    });
    
    // Get other errors that aren't specifically handled
    const otherErrors = computed(() => {
      const handledErrors = ['effectiveDate', 'signature', 'signatureDate', 'submission'];
      return Object.keys(props.errors)
        .filter(key => !handledErrors.includes(key) && props.errors[key])
        .reduce((obj, key) => {
          obj[key] = props.errors[key];
          return obj;
        }, {});
    });
    
    // Format error keys for display
    const formatErrorKey = (key) => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase());
    };
    
    return {
      minDate,
      hasVisibleErrors,
      otherErrors,
      formatErrorKey
    };
  }
};
</script>

<style scoped>
.form-field {
  @apply mb-6;
}

.flex-responsive-col {
  @apply flex flex-col sm:flex-row sm:justify-between;
}

.btn-responsive-secondary {
  @apply px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition-colors;
}

/* .input-responsive class is no longer needed here as ResponsiveDateInput handles its own styling,
   and the textarea for comments has its own .input-responsive class. 
   Keeping the class for the textarea as it's not replaced. */
.input-responsive {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm;
  @apply py-3 px-4 text-base sm:py-2 sm:px-3 sm:text-sm;
  @apply focus:outline-none;
}

.space-y-responsive {
  @apply space-y-4 md:space-y-6;
}

.mb-responsive {
  @apply mb-4 md:mb-6;
}

.mt-responsive {
  @apply mt-4 md:mt-6;
}

.text-responsive-base {
  @apply text-lg md:text-xl;
}

.mobile-enhanced-button {
  @apply py-3 px-4 text-base sm:py-2 sm:px-3 sm:text-sm;
}
</style> 